/**
 * Live2D Parameter Mapper
 * Handles parameter discovery and mapping for different model formats
 */

import { createLogger } from '../utils/logger';
import type { Live2DModel } from '../types/live2d';

export class ParameterMapper {
  private logger: ReturnType<typeof createLogger>;
  private parameterCache = new Map<string, number>();

  constructor() {
    this.logger = createLogger('ParameterMapper');
  }

  /**
   * Find a parameter by trying multiple common names
   */
  findParameter(model: Live2DModel, parameterNames: string[]): number {
    if (!model?.internalModel?.coreModel) return -1;

    // Check cache first
    const cacheKey = parameterNames.join('|');
    if (this.parameterCache.has(cacheKey)) {
      return this.parameterCache.get(cacheKey)!;
    }

    // Try to find parameter
    for (const paramName of parameterNames) {
      const paramIndex = model.internalModel.coreModel.getParameterIndex(paramName);
      if (paramIndex > -1) {
        this.logger.debug(`Found parameter: ${paramName} at index ${paramIndex}`);
        this.parameterCache.set(cacheKey, paramIndex);
        return paramIndex;
      }
    }

    this.logger.warn(`No parameter found for names: ${parameterNames.join(', ')}`);
    this.parameterCache.set(cacheKey, -1);
    return -1;
  }

  /**
   * Set parameter value if it exists
   */
  setParameterIfExists(model: Live2DModel, parameterNames: string[], value: number): boolean {
    const paramIndex = this.findParameter(model, parameterNames);
    if (paramIndex === -1) return false;

    try {
      model.internalModel.coreModel.setParameterValueByIndex(paramIndex, value);
      return true;
    } catch (error) {
      this.logger.error(`Error setting parameter ${parameterNames[0]}:`, error);
      return false;
    }
  }

  /**
   * Get parameter value if it exists
   */
  getParameterIfExists(model: Live2DModel, parameterNames: string[]): number | null {
    const paramIndex = this.findParameter(model, parameterNames);
    if (paramIndex === -1) return null;

    try {
      return model.internalModel.coreModel.getParameterValueByIndex(paramIndex);
    } catch (error) {
      this.logger.error(`Error getting parameter ${parameterNames[0]}:`, error);
      return null;
    }
  }

  /**
   * Discover and log all available parameters (useful for debugging)
   */
  logAllParameters(model: Live2DModel): string[] {
    if (!model?.internalModel?.coreModel) {
      this.logger.warn('Cannot log parameters: model not loaded');
      return [];
    }

    const parameters: string[] = [];
    const paramCount = model.internalModel.coreModel.getParameterCount();
    
    this.logger.info(`Model has ${paramCount} parameters:`);
    for (let i = 0; i < paramCount; i++) {
      const paramName = model.internalModel.coreModel.getParameterName(i);
      const paramValue = model.internalModel.coreModel.getParameterValueByIndex(i);
      parameters.push(paramName);
      this.logger.info(`  ${i}: ${paramName} = ${paramValue}`);
    }

    return parameters;
  }

  /**
   * Get standard parameter mappings used across Phantasy implementations
   */
  getStandardMappings() {
    return {
      // Mouth parameters for lip sync
      mouth: [
        'ParamMouthOpenY',
        'PARAM_MOUTH_OPEN_Y',
        'Param_Mouth_Open_Y',
        'ParamMouthForm',
        'PARAM_MOUTH_FORM',
        'Param_Mouth_Form',
        'ParamMouthOpen',
        'PARAM_MOUTH_OPEN',
        'Param_Mouth_Open',
      ],
      
      // Eye parameters
      leftEye: [
        'ParamEyeLOpen',
        'PARAM_EYE_L_OPEN',
        'Param_Eye_L_Open',
      ],
      rightEye: [
        'ParamEyeROpen',
        'PARAM_EYE_R_OPEN',
        'Param_Eye_R_Open',
      ],
      eyeBallX: [
        'ParamEyeBallX',
        'PARAM_EYE_BALL_X',
        'Param_Eye_Ball_X',
      ],
      eyeBallY: [
        'ParamEyeBallY',
        'PARAM_EYE_BALL_Y',
        'Param_Eye_Ball_Y',
      ],
      
      // Angle parameters
      angleX: [
        'ParamAngleX',
        'PARAM_ANGLE_X',
        'Param_Angle_X',
      ],
      angleY: [
        'ParamAngleY',
        'PARAM_ANGLE_Y',
        'Param_Angle_Y',
      ],
      angleZ: [
        'ParamAngleZ',
        'PARAM_ANGLE_Z',
        'Param_Angle_Z',
      ],
      
      // Body parameters
      bodyAngleX: [
        'ParamBodyAngleX',
        'PARAM_BODY_ANGLE_X',
        'Param_Body_Angle_X',
      ],
      bodyAngleY: [
        'ParamBodyAngleY',
        'PARAM_BODY_ANGLE_Y',
        'Param_Body_Angle_Y',
      ],
      bodyAngleZ: [
        'ParamBodyAngleZ',
        'PARAM_BODY_ANGLE_Z',
        'Param_Body_Angle_Z',
      ],
      
      // Breathing parameter
      breath: [
        'ParamBreath',
        'PARAM_BREATH',
        'Param_Breath',
      ],
    };
  }

  /**
   * Reset model to neutral position
   */
  resetToNeutral(model: Live2DModel): void {
    if (!model?.internalModel?.coreModel) return;

    const mappings = this.getStandardMappings();
    
    // Reset all angle parameters to 0
    this.setParameterIfExists(model, mappings.angleX, 0);
    this.setParameterIfExists(model, mappings.angleY, 0);
    this.setParameterIfExists(model, mappings.angleZ, 0);
    this.setParameterIfExists(model, mappings.bodyAngleX, 0);
    this.setParameterIfExists(model, mappings.bodyAngleY, 0);
    this.setParameterIfExists(model, mappings.bodyAngleZ, 0);
    
    // Reset eye position to center
    this.setParameterIfExists(model, mappings.eyeBallX, 0);
    this.setParameterIfExists(model, mappings.eyeBallY, 0);
    
    // Reset mouth to closed
    this.setParameterIfExists(model, mappings.mouth, 0);

    this.logger.debug('Model reset to neutral position');
  }

  /**
   * Clear parameter cache (useful when switching models)
   */
  clearCache(): void {
    this.parameterCache.clear();
    this.logger.debug('Parameter cache cleared');
  }
}